import {
  ELSTokenHelper,
  ELSRestClientHelper,
} from '@els/els-ui-common-react';
import {
  buildUrlFromBase,
  eolsBaseApi,
  withApiErrorLoggingOnly
} from '../../utilities/api.utilities';
import { InstructorCreatedStudentTokenAppParams } from './eols-token-service.dtos';
import { ServicePath } from '../../components/app/app.constants';
import { Application } from '../eols-app-link/eols-app-link.constants';
import { UserDto } from '../eols-user-management-service/eols-user-management-service.dtos';
import { TokenRotationResponse } from '../../types/token.types';

export const getStudentTokenAsInstructor = (
  student: UserDto,
  courseId: number,
  isbns: string
): Promise<{
  token: string;
}> => {
  const appParams: InstructorCreatedStudentTokenAppParams = {
    courseId,
    appId: Application.NHE_AI.toLowerCase(),
    isbns,
    authorizedIsbns: isbns,
    insUserEmail: ELSTokenHelper.getUserEmail(),
    isInstructorCreatedStudentToken: true
  };
  return withApiErrorLoggingOnly(() => {
    return eolsBaseApi.post(
      buildUrlFromBase(`${ServicePath.tokenServicePath}/masqueradeWithAppParams`),
      {
        data: {
          user: student,
          appParams
        }
      }
    );
  });
};

/**
 * Helper to call v2 token service without Authorization header
 * The v2 token endpoints authenticate using the refresh token in the request body,
 * not the JWT in the Authorization header (since the JWT is likely expired).
 */
const callTokenServiceWithoutAuth = <T = unknown>(
  method: 'put' | 'delete',
  refreshToken: string
): Promise<T> => {
  const url = buildUrlFromBase(ServicePath.tokenServiceV2Path);
  const options = {
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json, text/plain, */*',
      // Explicitly no Authorization header - the refresh token in body is the auth
    },
    useWithCredentials: false,
    data: { refreshToken },
  };

  return withApiErrorLoggingOnly(() => {
    return ELSRestClientHelper[method](url, options);
  });
};

/**
 * Exchange/rotate refresh token for new JWT + refresh token (PUT)
 * Used for both initial exchange after launch and subsequent rotations when JWT expires.
 *
 * NOTE: This endpoint does NOT use Authorization header - it authenticates via refresh token in body.
 */
export const putRefreshToken = (refreshToken: string): Promise<TokenRotationResponse> => {
  return callTokenServiceWithoutAuth<TokenRotationResponse>('put', refreshToken);
};

/**
 * Revoke refresh token (DELETE)
 * Used on explicit logout.
 *
 * NOTE: This endpoint does NOT use Authorization header - it authenticates via refresh token in body.
 */
export const revokeRefreshToken = (refreshToken: string): Promise<void> => {
  return callTokenServiceWithoutAuth<void>('delete', refreshToken);
};
